import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { IChecklistSchedule } from '@fi-shared/checklist/checklist-v2';
import { FiChecklist2ScheduleService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule.service';
import { ObjectTypes } from '@fi-shared/types';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import {
    IAccessRightCoreFilter,
    IAccessRightFilterCollection,
    IAccessRightSelectionFiltersCollection,
} from '@fi-shared/models';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { NgModel } from '@angular/forms';
import { FiScrollToElementAnimateService } from '@fi-shared/fi-shared-services/scroll-to-element-animate.service';
import { cloneDeep, FiDateTimeFormatOptions, isDefined } from '@fi-shared/utils';
import { Subject } from 'rxjs';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { FiChecklist2ScheduleFactoryService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-factory.service';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';

interface IScheduleEditFormErrorData {
    errorElement: HTMLElement;
    dialogMessageText: string;
}

@Component({
    selector: 'fi-checklist2-schedule-edit',
    templateUrl: './schedule-edit.component.html',
    styleUrls: ['./schedule-edit.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-checklist2-schedule-edit' },
    standalone: false,
})
export class FiChecklist2ScheduleEditComponent implements OnInit {
    @HostBinding('class.actions-offset-bottom') @Input() isChatAvailable: boolean;
    @Input({ required: true }) auditMode: boolean;
    @Input() checklistSchedule: IChecklistSchedule;
    @Input() lightWeightChecklists: INameIdObj[];
    @Input() accessRightFilterCollection: IAccessRightFilterCollection;
    @Input() savedQuickSelectionFilters: IAccessRightSelectionFiltersCollection[] = [];

    @ViewChild(FiValidatingFormDirective) scheduleEditorForm: FiValidatingFormDirective;
    /*@ViewChild('fiChecklist2ScheduleEditUnitAssignmentComponent', { read: ElementRef }) unitAssignmentComponentRef: ElementRef;
    @ViewChild('fiChecklist2ScheduleEditGeneralInfoComponent', { read: ElementRef}) generalInfoComponent: ElementRef;
    @ViewChild('fiChecklist2ScheduleEditScheduleTimeComponent', { read: ElementRef}) scheduleTimeComponent: ElementRef;*/
    ObjectTypes = ObjectTypes;

    defaultContentLanguageId: number;
    selectedContentLanguageId: number;
    checklistScheduleName: string;

    constructor(
        private fiChecklist2ScheduleService: FiChecklist2ScheduleService,
        private fiUserDataService: FiUserDataService,
        private fiCustomerDataService: FiCustomerDataService,
        private fiFormatDateService: FiFormatDateService,
        private saveChangesWarningService: SaveChangesWarningService,
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private fiStateService: FiStateService,
        private contentLanguageSharedService: FiContentLanguageSharedService,
        private fiScrollToElementAnimateService: FiScrollToElementAnimateService,
        private fiChecklistHelperService: FiChecklistHelperService,
        private fiChecklist2ScheduleFactoryService: FiChecklist2ScheduleFactoryService,
    ) {}

    ngOnInit(): void {
        this.setLanguagesData();
        this.setChecklistScheduleName();
        this.setDefaultRemindTime();

        if (this.showDebugInfo()) {
            this.checklistSchedule.DebugInfo.Periods.forEach((period) => {
                console.log(
                    'Period ' + period.Id,
                    this.showDateForDebug(period.StartDateTime) + ' - ' + this.showDateForDebug(period.DueDateTime),
                );
            });
        }

        this.setChangeWatcher();
    }

    private setChangeWatcher() {
        setTimeout(() => {
            if (this.checklistSchedule.Id) {
                this.startWarningChanges();
                return;
            }

            const scheduleCopy = cloneDeep(this.checklistSchedule);
            this.checklistSchedule = this.fiChecklist2ScheduleFactoryService.getChecklistScheduleInstance(
                this.auditMode,
            );
            this.startWarningChanges();
            this.checklistSchedule = cloneDeep(scheduleCopy);
        });
    }

    private startWarningChanges() {
        this.saveChangesWarningService.start(
            () => this.checklistSchedule,
            this.fiChecklistHelperService.getChecklistDiscardChangesDialogData(
                this.checklistSchedule.Id,
                ObjectTypes.ChecklistSchedule,
            ),
        );
    }

    private showDebugInfo() {
        return this.checklistSchedule.DebugInfo && this.fiCustomerDataService.isTestCustomer();
    }
    private showDateForDebug(dateStr: string) {
        if (!dateStr) {
            return '-';
        }
        return this.fiFormatDateService.formatDate(dateStr, FiDateTimeFormatOptions, {
            showToday: false,
        });
    }

    private setLanguagesData() {
        this.defaultContentLanguageId = this.fiUserDataService.getContentLanguage();
        this.selectedContentLanguageId = this.contentLanguageSharedService.getContentLanguageIdByContentTranslation(
            this.checklistSchedule.Names,
        );
    }

    private setChecklistScheduleName() {
        this.checklistScheduleName =
            this.checklistSchedule.Name ??
            this.checklistSchedule.Names.find((name) => {
                return name.ContentLanguageId === this.defaultContentLanguageId;
            })?.Value;
    }

    private setDefaultRemindTime() {
        if (!isDefined(this.checklistSchedule.RemindTime) || !Object.keys(this.checklistSchedule.RemindTime).length) {
            this.checklistSchedule.RemindTime = { Month: 0, Day: 0, Hour: 0, Minute: 0 };
        }
    }

    goBack() {
        window.history.back();
    }

    save() {
        this.fiChecklist2ScheduleService.saveChecklistSchedule(this.checklistSchedule, this.auditMode).then(() => {
            this.saveChangesWarningService.stop();
            this.fiStateService.go(FiStateNames.getChecklistV2RootState(this.auditMode).Schedule.List);
        });
    }

    onFormValidateError(errorModels: NgModel[]) {
        /*const scrollingElement = this.unitAssignmentComponentRef.nativeElement.parentElement;
        const scheduleErrorData: IScheduleEditFormErrorData = this.getScheduleEditFormErrorData(errorModels);
        this.fiScrollToElementAnimateService.scrollToElementIntoView(scheduleErrorData.errorElement, scrollingElement);

        this.fiDialogsService.notify({
            headerText: this.appTextService.get('common.warning'),
            messageText: scheduleErrorData.dialogMessageText
        });*/

        this.fiDialogsService.notify({
            headerText: this.appTextService.get('common.warning'),
            messageText: this.appTextService.get('common.error_all_mandatory'),
            config: {
                panelClass: 'fi-rounded-dialog-panel',
                autoFocus: false,
                restoreFocus: false,
            },
        });
    }

    cloneScheduleAccessRightCoreFilter(newAccessRightCoreFilter: IAccessRightCoreFilter) {
        this.checklistSchedule.AccessRightFilterCollection.AccessRightCoreFilter = cloneDeep(newAccessRightCoreFilter);
    }

    triggerUpdate$: Subject<void> = new Subject<void>();
    triggerUpdate() {
        this.triggerUpdate$.next();
        this.scheduleEditorForm?.updateValidity();
    }

    isSaveButtonDisabled(): boolean {
        return !!this.scheduleEditorForm?.errors?.publicationMinRangeFit;
    }

    private isComponentContainingError(errorModels: NgModel[], components: any[]): NgModel {
        return errorModels.find((errorModel) => {
            return components.some((component) => errorModel.valueAccessor instanceof component);
        });
    }
}
